<script setup>
import { computed, ref, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";

const loading = ref(false);
const roles = ref([]);
const permissions = ref([]);
const searchQuery = ref("");
const searchPermission = ref("");

// Edit/Add state
const isEditing = ref(false);
const editRoleRecord = ref({
  _id: "",
  name: "",
  enabledPermissions: [],
});

// Dialog state
const addPermissionDialog = ref(false);
const permissionsToAdd = ref([]);

// Computed
const availablePermissionsToAdd = computed(() => {
  return permissionsToAdd.value.map((p) => ({
    ...p,
    actions: p.actions.map((a) => ({
      action: a,
      enabled: false,
    })),
  }));
});

// API calls
async function getRoles() {
  loading.value = true;
  try {
    const result = await apiQuery("J24Auth", "roles", { apiAction: "get" });
    roles.value = result;
  } catch (error) {
    console.error("Error fetching roles:", error);
  }
  loading.value = false;
}

async function getPermissions() {
  if (!permissions.value.length) {
    loading.value = true;
    try {
      const result = await apiQuery("J24Auth", "getpermission", "");
      permissions.value = result;
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
    loading.value = false;
  }

  permissionsToAdd.value = permissions.value.map((p) => ({
    ...p,
    actions: p.actions.map((action) => ({
      action,
      enabled: false,
    })),
  }));
  addPermissionDialog.value = true;
}

// Actions
function newRolePressed() {
  editRoleRecord.value = {
    _id: "",
    name: "",
    enabledPermissions: [],
  };
  isEditing.value = true;
}

function editRolePressed(role) {
  editRoleRecord.value = {
    _id: role._id,
    name: role.name,
    enabledPermissions: [...role.enabledPermissions],
  };
  isEditing.value = true;
}

function cancelEdit() {
  editRoleRecord.value = {
    _id: "",
    name: "",
    enabledPermissions: [],
  };
  isEditing.value = false;
  addPermissionDialog.value = false;
  permissionsToAdd.value = [];
}

function deletePermission(index) {
  editRoleRecord.value.enabledPermissions.splice(index, 1);
}

function savePermissionsToAdd() {
  permissionsToAdd.value.forEach((permission) => {
    const enabledActions = permission.actions
      .filter((a) => a.enabled)
      .map((a) => a.action);

    if (enabledActions.length > 0) {
      editRoleRecord.value.enabledPermissions.push({
        permissionName: permission.permissionName,
        app: permission.app,
        actions: enabledActions,
      });
    }
  });
  addPermissionDialog.value = false;
  permissionsToAdd.value = [];
}

async function saveRole() {
  loading.value = true;
  const action = editRoleRecord.value._id ? "update" : "add";

  const payload = {
    apiAction: action,
    name: editRoleRecord.value.name,
    enabledPermissions: editRoleRecord.value.enabledPermissions,
    deleted: false,
  };

  if (action === "update") {
    payload._id = editRoleRecord.value._id;
  }

  await apiQuery("J24Auth", "roles", payload);
  cancelEdit();
  await getRoles();
  loading.value = false;
}

async function deleteRole() {
  if (!confirm("Are you sure you want to delete this role?")) return;

  loading.value = true;
  await apiQuery("J24Auth", "roles", {
    apiAction: "update",
    _id: editRoleRecord.value._id,
    name: editRoleRecord.value.name,
    enabledPermissions: editRoleRecord.value.enabledPermissions,
    deleted: true,
  });
  cancelEdit();
  await getRoles();
  loading.value = false;
}

onMounted(() => {
  getRoles();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Roles Management</h1>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          variant="elevated"
          @click="newRolePressed()"
          :disabled="isEditing"
        >
          Add New Role
        </v-btn>
      </v-col>
    </v-row>

    <!-- Edit/Add Form -->
    <v-card v-if="isEditing" class="my-4">
      <v-card-title>
        {{ editRoleRecord._id ? "Edit Role" : "New Role" }}
      </v-card-title>
      <v-card-text>
        <v-form>
          <v-text-field
            v-model="editRoleRecord.name"
            label="Role Name"
            variant="outlined"
            density="comfortable"
          />

          <!-- Permissions Section -->
          <v-divider class="my-4" />
          <div class="d-flex align-center mb-2">
            <h3>Permissions</h3>
            <v-spacer />
            <v-btn
              size="small"
              icon="mdi-plus"
              variant="elevated"
              @click="getPermissions()"
            />
          </div>

          <v-data-table
            :headers="[
              { title: 'App', key: 'app' },
              { title: 'Permission', key: 'permissionName' },
              { title: 'Actions', key: 'actions' },
              { title: '', key: 'delete', sortable: false },
            ]"
            :items="editRoleRecord.enabledPermissions"
            density="compact"
          >
            <template v-slot:[`item.actions`]="{ item }">
              {{ item.actions.join(", ") }}
            </template>
            <template v-slot:[`item.delete`]="{ index }">
              <v-btn
                size="small"
                icon="mdi-delete"
                variant="text"
                color="error"
                @click="deletePermission(index)"
              />
            </template>
            <template v-slot:[`no-data`]>
              <div class="text-center pa-4">No permissions added yet</div>
            </template>
          </v-data-table>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="editRoleRecord._id"
          color="error"
          variant="elevated"
          @click="deleteRole()"
        >
          Delete Role
        </v-btn>
        <v-spacer />
        <v-btn variant="elevated" @click="cancelEdit()">Cancel</v-btn>
        <v-btn color="primary" variant="elevated" @click="saveRole()"
          >Save Role</v-btn
        >
      </v-card-actions>
    </v-card>

    <!-- Roles List -->
    <v-card v-else>
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Search roles"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
        />
      </v-card-text>
      <v-data-table
        :headers="[
          { title: 'Role Name', key: 'name' },
          { title: 'Permissions', key: 'permissionsCount' },
          { title: 'Actions', key: 'actions', sortable: false },
        ]"
        :items="roles"
        :loading="loading"
        :search="searchQuery"
        :items-per-page="10"
      >
        <template v-slot:[`item.permissionsCount`]="{ item }">
          {{ item.enabledPermissions?.length || 0 }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            size="small"
            icon="mdi-pencil"
            variant="text"
            @click="editRolePressed(item)"
          />
        </template>
        <template v-slot:expanded-row="{ item }">
          <tr>
            <td colspan="3" class="pa-4">
              <v-list density="compact">
                <v-list-subheader>Enabled Permissions</v-list-subheader>
                <v-list-item
                  v-for="(permission, idx) in item.enabledPermissions"
                  :key="idx"
                >
                  <v-list-item-title>
                    <strong>{{ permission.app }}</strong> -
                    {{ permission.permissionName }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ permission.actions.join(", ") }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Permissions Dialog -->
    <v-dialog v-model="addPermissionDialog" max-width="1000" scrollable>
      <v-card>
        <v-card-title>Add Permissions</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="searchPermission"
            prepend-inner-icon="mdi-magnify"
            label="Search Permissions"
            variant="outlined"
            density="comfortable"
            clearable
            class="mb-4"
          />
          <v-data-table
            :headers="[
              { title: 'App', key: 'app', sortable: true },
              { title: 'Permission', key: 'permissionName', sortable: true },
              { title: 'Actions', key: 'actions', sortable: false },
            ]"
            :items="permissionsToAdd"
            :search="searchPermission"
            density="compact"
            :items-per-page="10"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex align-center flex-wrap ga-2">
                <v-btn
                  size="x-small"
                  variant="elevated"
                  @click="
                    item.actions.forEach((a) => {
                      if (a.action !== 'Deny') a.enabled = true;
                    })
                  "
                >
                  Select All
                </v-btn>
                <v-checkbox
                  v-for="action in item.actions"
                  :key="action.action"
                  v-model="action.enabled"
                  :label="action.action"
                  hide-details
                  density="compact"
                  class="ma-0"
                />
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="elevated" @click="addPermissionDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="savePermissionsToAdd()"
          >
            Add Selected Permissions
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading" persistent max-width="300">
      <v-card color="grey">
        <v-card-text>
          Loading
          <v-progress-linear indeterminate color="white" class="mb-0" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
